import { HttpErrorResponse } from '@angular/common/http';

import { ApiClientError, type ApiError, type ApiErrorDetail } from './api-error.types';

type ErrorPayload = {
  code?: unknown;
  detail?: unknown;
  details?: unknown;
  message?: unknown;
  path?: unknown;
  requestId?: unknown;
  status?: unknown;
  timestamp?: unknown;
};

function mapHttpErrorResponse(error: HttpErrorResponse): ApiError {
  const payload = toErrorPayload(error.error);
  const status = error.status || getFallbackStatus(error);

  return {
    code: typeof payload.code === 'string' ? payload.code : getErrorCodeFromStatus(status),
    details: toApiErrorDetails(payload.details),
    message: typeof payload.message === 'string'
      ? payload.message
      : typeof payload.detail === 'string'
        ? payload.detail
        : getDefaultMessage(status, error),
    path: typeof payload.path === 'string' ? payload.path : error.url ?? undefined,
    requestId: typeof payload.requestId === 'string' ? payload.requestId : undefined,
    status,
    timestamp: typeof payload.timestamp === 'string' ? payload.timestamp : undefined,
  };
}

export function toApiClientError(error: unknown) {
  if (error instanceof ApiClientError) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    return new ApiClientError(mapHttpErrorResponse(error));
  }

  return new ApiClientError({
    code: 'unknown_error',
    details: [],
    message: error instanceof Error ? error.message : 'Unexpected error.',
    status: 500,
  });
}

function toErrorPayload(value: unknown): ErrorPayload {
  return value && typeof value === 'object' ? (value as ErrorPayload) : {};
}

function toApiErrorDetails(value: unknown): readonly ApiErrorDetail[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((detail) => {
    if (!detail || typeof detail !== 'object') {
      return [];
    }

    const detailRecord = detail as Record<string, unknown>;

    if (typeof detailRecord['code'] !== 'string' || typeof detailRecord['message'] !== 'string') {
      return [];
    }

    return [{
      code: detailRecord['code'],
      field: typeof detailRecord['field'] === 'string' ? detailRecord['field'] : undefined,
      message: detailRecord['message'],
    }];
  });
}

function getFallbackStatus(error: HttpErrorResponse) {
  return error.status === 0 ? 0 : 500;
}

function getErrorCodeFromStatus(status: number) {
  switch (status) {
    case 0:
      return 'network_error';
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'validation_error';
    default:
      return status >= 500 ? 'server_error' : 'unknown_error';
  }
}

function getDefaultMessage(status: number, error: HttpErrorResponse) {
  if (status === 0) {
    return 'Network error. Check your connection and try again.';
  }

  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }

  if (status >= 500) {
    return 'Server error. Please try again later.';
  }

  return 'Request failed.';
}
